/**
 * Generator registry and app-plan seeds.
 *
 * App plans are code constants — never stored in the DB.
 * The active-plan lookup checks id.startsWith("app-") before querying user plans.
 */

import { PLAN_TEMPLATES, STANDARD_TEMPLATE } from "./plan-templates.js";
import { FsrsPlanGenerator } from "./fsrs-plan-generator.js";
import type { PlanGenerator, SavedPlan } from "./plan-generator.js";
import { resolveNodes } from "./plan-generator.js";
import type { PlanTemplate } from "./plan-template.js";

// ─── Registry ─────────────────────────────────────────────────────────────────

export const PLAN_GENERATORS: ReadonlyMap<string, PlanGenerator> = new Map(
  PLAN_TEMPLATES.map((t) => [`fsrs-${t.id}`, new FsrsPlanGenerator(t)] as const),
);

export function resolveGenerator(generatorId: string): PlanGenerator {
  return PLAN_GENERATORS.get(generatorId) ?? PLAN_GENERATORS.get("fsrs-standard")!;
}

// ─── App plan seeds ───────────────────────────────────────────────────────────

const APP_PLAN_SEED_DATE = "2026-01-01T00:00:00.000Z";

export const APP_PLANS: readonly SavedPlan[] = [
  {
    id: "app-standard",
    name: "Standard",
    source: "app",
    generatorId: "fsrs-standard",
    sessionMix: "balanced",
    focusTracks: null,
    focusNodeIds: null,
    configOverrides: null,
    generatorState: null,
    createdAt: APP_PLAN_SEED_DATE,
    updatedAt: APP_PLAN_SEED_DATE,
  },
  {
    id: "app-intensive",
    name: "Intensive",
    source: "app",
    generatorId: "fsrs-intensive",
    sessionMix: "balanced",
    focusTracks: null,
    focusNodeIds: null,
    configOverrides: null,
    generatorState: null,
    createdAt: APP_PLAN_SEED_DATE,
    updatedAt: APP_PLAN_SEED_DATE,
  },
  {
    id: "app-review-sprint",
    name: "Review Sprint",
    source: "app",
    generatorId: "fsrs-review-sprint",
    sessionMix: "review-only",
    focusTracks: null,
    focusNodeIds: null,
    configOverrides: null,
    generatorState: null,
    createdAt: APP_PLAN_SEED_DATE,
    updatedAt: APP_PLAN_SEED_DATE,
  },
  {
    id: "app-harmony-focus",
    name: "Harmony Focus",
    source: "app",
    generatorId: "fsrs-harmony-focus",
    sessionMix: "balanced",
    focusTracks: ["harmony", "intervals"],
    focusNodeIds: null,
    configOverrides: null,
    generatorState: null,
    createdAt: APP_PLAN_SEED_DATE,
    updatedAt: APP_PLAN_SEED_DATE,
  },
  {
    id: "app-pitch-and-modes",
    name: "Pitch & Modes",
    source: "app",
    generatorId: "fsrs-pitch-and-modes",
    sessionMix: "balanced",
    focusTracks: ["pitch", "modes"],
    focusNodeIds: null,
    configOverrides: null,
    generatorState: null,
    createdAt: APP_PLAN_SEED_DATE,
    updatedAt: APP_PLAN_SEED_DATE,
  },
];

export const APP_PLAN_IDS = new Set(APP_PLANS.map((p) => p.id));

// ─── effectivePlanTemplate ────────────────────────────────────────────────────

/**
 * Derive the effective PlanTemplate for any SavedPlan, applying focusTracks /
 * focusNodeIds filtering and configOverrides. Used by the Why / Explain pages
 * to feed buildCoachPlanWithTrace with the same node set that generator.build()
 * uses. Returns STANDARD_TEMPLATE for non-FSRS generators.
 */
export function effectivePlanTemplate(generator: PlanGenerator, plan: SavedPlan): PlanTemplate {
  if (!(generator instanceof FsrsPlanGenerator)) return STANDARD_TEMPLATE;
  return {
    ...generator.template,
    nodes: resolveNodes(generator.template.nodes, plan),
    config: plan.configOverrides
      ? { ...generator.template.config, ...plan.configOverrides }
      : generator.template.config,
  };
}

// ─── resolveActivePlan ────────────────────────────────────────────────────────

const FALLBACK_PLAN = APP_PLANS[0]!;

export function resolveActivePlan(id: string, userPlans: readonly SavedPlan[]): SavedPlan {
  if (id.startsWith("app-")) {
    return APP_PLANS.find((p) => p.id === id) ?? FALLBACK_PLAN;
  }
  return userPlans.find((p) => p.id === id) ?? FALLBACK_PLAN;
}
